import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Button, TextField, MenuItem, Box, CircularProgress, FormControl, InputLabel, Select, FormHelperText, Alert, Snackbar } from '@mui/material';
import {
  useAppointmentsPartialUpdateMutation,
  useTeamsTeamMembersListQuery,
  useTeamsTeamMembersByServiceSalonRetrieveQuery,
  useServicesSalonListQuery,
  useUsersClientsListQuery,
  useSalonsRetrieveQuery,
  useSalonsMySalonsRetrieveQuery,
  useAppointmentsAvailableSlotsRetrieveQuery
} from '../../../services/GLOWClient';
import { usePermissionControlledSchedulesQuery } from '../../../hooks';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import 'moment-timezone';
import { format } from 'date-fns';
import { useModifyAndConfirmAppointmentMutation } from '../../../services/appointmentWorkflowApi';

// Updated to use native datetime-local inputs instead of MUI DateTimePicker
const UpdateAppointment = ({ open, onClose, onSuccess, appointment, confirmOnSave = false }) => {
  const { t } = useTranslation();

  const statusOptions = [
    { value: 'pending', label: t('pro_dashboard.status.pending') },
    { value: 'confirmed', label: t('pro_dashboard.status.confirmed') },
    { value: 'cancelled', label: t('pro_dashboard.status.cancelled') },
    { value: 'completed', label: t('pro_dashboard.status.completed') },
  ];

  const [dialogClosed, setDialogClosed] = useState(false);
  const [updateAppointment, { isLoading: isLegacyUpdating }] = useAppointmentsPartialUpdateMutation();
  const [modifyAndConfirm, { isLoading: isWorkflowUpdating }] = useModifyAndConfirmAppointmentMutation();
  const isUpdating = isLegacyUpdating || isWorkflowUpdating;
  const [validationMessage, setValidationMessage] = useState('');
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [fieldErrors, setFieldErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    start_time: '',
    end_time: '',
    status: '',
    client: null,
    team_member: null,
    service: null,
    salon: null,
    // Additional display fields
    client_name: '',
    team_member_name: '',
    service_name: '',
    salon_name: '',
    cancellation_reason: ''
  });


  // Fetch all required data (always fetch, don't skip)
  const { data: clientsData, isLoading: clientsLoading } = useUsersClientsListQuery();
  const { data: salonsData, isLoading: salonsLoading } = useSalonsMySalonsRetrieveQuery();

  // Fetch team members filtered by service and salon
  const { data: filteredTeamMembers, isLoading: filteredTeamMembersLoading } =
    useTeamsTeamMembersByServiceSalonRetrieveQuery(
      { salon: formData.salon, service: formData.service },
      { skip: !formData.salon || !formData.service }
    );

  // Fallback to all team members if no service is selected yet
  const { data: allTeamMembersData, isLoading: allTeamMembersLoading } = useTeamsTeamMembersListQuery(undefined, {
    skip: !!(formData.salon && formData.service) // Skip if we have both salon and service (use filtered instead)
  });

  // Use filtered team members when available, otherwise use all team members
  const teamMembersData = (formData.salon && formData.service && filteredTeamMembers)
    ? filteredTeamMembers
    : allTeamMembersData;
  const teamMembersLoading = (formData.salon && formData.service)
    ? filteredTeamMembersLoading
    : allTeamMembersLoading;

  const { data: servicesData, isLoading: servicesLoading } = useServicesSalonListQuery(
    formData.salon ? { salonId: formData.salon } : undefined,
    { skip: !formData.salon }
  );

  // These can stay conditional
  // Désactivé temporairement car cause des erreurs 404 quand formData.salon est undefined
  // const { data: salonDetails, isLoading: salonDetailsLoading } = useSalonsRetrieveQuery(
  //   { id: formData.salon },
  //   { skip: !formData.salon }
  // );
  const salonDetails = null; // Temporairement désactivé

  const { data: teamMemberSchedule, isLoading: scheduleLoading } = usePermissionControlledSchedulesQuery(
    formData.team_member ? { teamMemberId: formData.team_member } : undefined,
    { skip: !formData.team_member }
  );

  // Fetch available slots when service and team member are selected
  const availableSlotsParams = useMemo(() => {
    if (!formData.service || !formData.team_member) return null;
    return {
      serviceId: formData.service,
      teamMemberId: formData.team_member,
      date: moment(selectedDate).format('YYYY-MM-DD')
    };
  }, [formData.service, formData.team_member, selectedDate]);

  // Use the RTK Query hook to fetch available slots
  const {
    data: availableSlotsData,
    isLoading: isLoadingSlots,
    error: slotsError,
    refetch: refetchAvailableSlots
  } = useAppointmentsAvailableSlotsRetrieveQuery(availableSlotsParams, {
    // Skip the query if we don't have the required parameters
    skip: !formData.service || !formData.team_member,
    // Refetch when the date changes
    refetchOnMountOrArgChange: true
  });

  // Update available slots when data is received
  useEffect(() => {
    if (availableSlotsData) {
      setAvailableSlots(availableSlotsData.available_slots || []);
    }
  }, [availableSlotsData]);

  // Log any errors
  useEffect(() => {
    if (slotsError) {
      console.error('Error fetching available slots:', slotsError);
    }
  }, [slotsError]);

  // Helper function to find ID by name
  const findIdByName = (data, nameField, name, idField) => {
    if (!data || !Array.isArray(data) || !name) return null;

    const item = data.find(item => {
      if (nameField.includes('.')) {
        // Handle nested properties like 'user_details.first_name'
        const parts = nameField.split('.');
        let value = item;
        for (const part of parts) {
          value = value?.[part];
        }
        return value === name;
      } else if (nameField === 'full_name') {
        // Handle full name matching for clients
        return `${item.first_name} ${item.last_name}` === name;
      } else {
        return item[nameField] === name;
      }
    });

    return item ? item[idField] : null;
  };

  // Reset dialog state and form data when open/close changes
  useEffect(() => {
    if (open === false) {
      setDialogClosed(true);
      setValidationMessage('');
      setIsFormInitialized(false);
    } else {
      setDialogClosed(false);

      // Initialize form with appointment data
      if (appointment) {
        console.log('UpdateAppointment - Initializing with appointment:', appointment);

        const formDataObj = {
          start_time: appointment.start_time || '',
          end_time: appointment.end_time || '',
          status: appointment.status || 'pending',
          // Use the name fields from the enhanced API response
          client_name: appointment.client_name || '',
          team_member_name: appointment.team_member_name || '',
          service_name: appointment.service_name || '',
          salon_name: appointment.salon_name || '',
          // IDs from the appointment - essayer plusieurs variantes
          client: appointment.client || appointment.client_id || null,
          team_member: appointment.team_member || appointment.team_member_id || null,
          service: appointment.service || appointment.service_id || null,
          salon: appointment.salon || appointment.salon_id || null,
          cancellation_reason: appointment.cancellation_reason || ''
        };

        console.log('UpdateAppointment - Form data initialized:', formDataObj);

        setFormData(formDataObj);
        setIsFormInitialized(false); // Reset initialization flag
      }
    }
  }, [open, appointment]);

  // Effect to resolve IDs from names once data is loaded
  useEffect(() => {
    if (!appointment || !open || isFormInitialized) return;

    // Check if all required base data is loaded (services are loaded after salon is selected)
    const baseDataLoaded = salonsData && clientsData && teamMembersData;
    if (!baseDataLoaded) return;


    const updatedFormData = { ...formData };
    let hasChanges = false;

    // Only resolve IDs if they're not already set

    // Resolve salon ID
    if (appointment.salon_name && !formData.salon) {
      // First try to use the ID directly from the appointment
      if (appointment.salon) {
        updatedFormData.salon = appointment.salon;
        hasChanges = true;
      } else {
        // Fall back to finding by name
        const salonId = findIdByName(salonsData, 'name', appointment.salon_name, 'salon_id');
        if (salonId) {
          updatedFormData.salon = salonId;
          hasChanges = true;
        }
      }
    }

    // Resolve client ID
    if (appointment.client_name && !formData.client) {
      // First try to use the ID directly from the appointment
      if (appointment.client) {
        updatedFormData.client = appointment.client;
        hasChanges = true;
      } else {
        // Fall back to finding by name
        const clientId = findIdByName(clientsData, 'full_name', appointment.client_name, 'id');
        if (clientId) {
          updatedFormData.client = clientId;
          hasChanges = true;
        }
      }
    }

    // Resolve team member ID
    if (appointment.team_member_name && !formData.team_member) {
      // First try to use the ID directly from the appointment
      if (appointment.team_member) {
        updatedFormData.team_member = appointment.team_member;
        hasChanges = true;
      } else {
        // Fall back to finding by name
        const teamMemberId = findIdByName(
          teamMembersData,
          'user_details.first_name',
          appointment.team_member_name.split(' ')[0], // First name
          'team_member_id'
        );
        if (teamMemberId) {
          updatedFormData.team_member = teamMemberId;
          hasChanges = true;
        }
      }
    }

    // Resolve service ID (only if salon is selected and services are loaded)
    if (formData.salon && servicesData && appointment.service_name && !formData.service) {
      // First try to use the ID directly from the appointment
      if (appointment.service) {
        updatedFormData.service = appointment.service;
        hasChanges = true;
      } else {
        // Fall back to finding by name
        const serviceId = findIdByName(servicesData, 'name', appointment.service_name.split(' - ')[0], 'service_id');
        if (serviceId) {
          updatedFormData.service = serviceId;
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      setFormData(updatedFormData);
      setIsFormInitialized(true);
    } else {
      // Even if no IDs were resolved, mark as initialized to prevent infinite loops
      setIsFormInitialized(true);
    }
  }, [appointment, open, salonsData, clientsData, teamMembersData, servicesData, isFormInitialized]);

  // Memoize selected service for performance
  const selectedService = useMemo(() => {
    return servicesData?.find(service => service.service_id === Number(formData.service));
  }, [formData.service, servicesData]);

  // Handle form field changes
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset service when salon changes
    if (field === 'salon') {
      setFormData(prev => ({
        ...prev,
        salon: value,
        service: null
      }));
    }
  };

  // Handle date change
  const handleDateChange = useCallback((event) => {
    const newDate = new Date(event.target.value);
    setSelectedDate(newDate);
    // RTK Query will automatically refetch when availableSlotsParams changes
  }, []);

  // Handle slot click
  const handleSlotClick = useCallback((slot) => {
    const [hours, minutes] = slot.split(':');
    const slotDate = new Date(selectedDate);
    slotDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    const isoString = moment(slotDate).format();
    setFormData(prev => {
      const updatedData = {
        ...prev,
        start_time: isoString
      };

      // If service is selected, update end time based on service duration
      if (selectedService) {
        const endTime = new Date(slotDate);
        endTime.setMinutes(endTime.getMinutes() + selectedService.duration);
        updatedData.end_time = moment(endTime).format();
      }

      return updatedData;
    });
    setValidationMessage('');
  }, [selectedDate, selectedService]);

  // Filter available slots for display
  const filteredAvailableSlots = useMemo(() => {
    if (!availableSlots?.length) return [];
    const today = new Date();
    const baseDate = selectedDate;

    return availableSlots
      .filter(slot => {
        // Filter out past hours for today
        const [hours, minutes] = slot.split(':');
        const slotDate = new Date(baseDate);
        slotDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

        // If it's today, only show future slots
        const isToday = slotDate.toDateString() === today.toDateString();

        if (isToday) {
          return slotDate > today;
        }

        // For future dates, show all slots
        return true;
      });
  }, [availableSlots, selectedDate]);

  // Helper function to check if time is within opening hours
  const isWithinOpeningHours = (date, openingHours) => {
    if (!openingHours) return true; // If no opening hours, assume always open

    // Get day of week in French
    const daysMap = {
      0: 'dimanche',
      1: 'lundi',
      2: 'mardi',
      3: 'mercredi',
      4: 'jeudi',
      5: 'vendredi',
      6: 'samedi'
    };

    const dayOfWeek = daysMap[moment(date).day()];
    const dayHours = openingHours[dayOfWeek];

    // Check if salon is closed that day
    if (!dayHours || dayHours.ouverture === 'fermé' || dayHours.fermeture === 'fermé') {
      return false;
    }

    // Parse opening and closing hours
    const appointmentTime = moment(date).format('HH:mm');
    const openingTime = dayHours.ouverture;
    const closingTime = dayHours.fermeture;

    // Compare times as strings (HH:mm format)
    return appointmentTime >= openingTime && appointmentTime <= closingTime;
  };

  // Helper function to check if time is within team member's schedule
  const isWithinTeamMemberSchedule = (date, schedule) => {
    if (!schedule || !schedule.length) return true; // If no schedule, assume always available

    // Get day of week in English (as stored in the backend)
    const daysMap = {
      0: 'sun',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday'
    };

    const dayOfWeek = daysMap[moment(date).day()];

    // Find schedule for this day
    const daySchedule = schedule.find(s => s.day_of_week === dayOfWeek);

    // If no schedule for this day, team member is not available
    if (!daySchedule) return false;

    // Parse appointment time, start time and end time
    const appointmentTime = moment(date).format('HH:mm');
    const startTime = moment(`2000-01-01T${daySchedule.start_time}`).format('HH:mm');
    const endTime = moment(`2000-01-01T${daySchedule.end_time}`).format('HH:mm');

    // Compare times as strings (HH:mm format)
    return appointmentTime >= startTime && appointmentTime <= endTime;
  };

  // Validate appointment times against salon hours and team member schedule
  const validateAppointmentTimes = () => {
    // Skip validation if required data is missing
    if (!formData.salon || !salonDetails || !formData.start_time || !formData.end_time) {
      return true;
    }

    // Check if appointment is within salon opening hours
    const startInOpeningHours = isWithinOpeningHours(formData.start_time, salonDetails.opening_hours);
    const endInOpeningHours = isWithinOpeningHours(formData.end_time, salonDetails.opening_hours);

    if (!startInOpeningHours || !endInOpeningHours) {
      setValidationMessage(t('pro_appointments.validation.outside_salon_hours'));
      return false;
    }

    // Check if appointment is within team member's schedule
    if (formData.team_member && teamMemberSchedule) {
      const startInSchedule = isWithinTeamMemberSchedule(formData.start_time, teamMemberSchedule);
      const endInSchedule = isWithinTeamMemberSchedule(formData.end_time, teamMemberSchedule);

      if (!startInSchedule || !endInSchedule) {
        setValidationMessage(t('pro_appointments.validation.outside_team_member_hours'));
        return false;
      }
    }

    return true;
  };

  const handleUpdate = async () => {
    if (!appointment || !appointment.appointment_id) {
      console.error('No appointment selected for update');
      return;
    }

    // Determine what has actually changed
    const hasTimeChange = (
      formData.start_time && formData.start_time !== appointment.start_time
    ) || (
        formData.end_time && formData.end_time !== appointment.end_time
      );
    const hasStatusChange = formData.status && formData.status !== appointment.status;
    const hasReasonChange = formData.status === 'cancelled' && formData.cancellation_reason !== appointment.cancellation_reason;
    const hasClientChange = Boolean(formData.client);
    const hasTeamMemberChange = Boolean(formData.team_member);
    const hasServiceChange = Boolean(formData.service);
    const hasSalonChange = Boolean(formData.salon);

    // If no changes, do nothing
    if (!hasTimeChange && !hasStatusChange && !hasReasonChange && !hasClientChange && !hasTeamMemberChange && !hasServiceChange && !hasSalonChange) {
      return;
    }

    // Validate appointment times only if times are being changed
    if (hasTimeChange && !validateAppointmentTimes()) {
      // Show local validation error in snackbar
      setSnackbarSeverity('error');
      setSnackbarMessage(validationMessage || t('pro_appointments.error.invalid_appointment'));
      setSnackbarOpen(true);
      return;
    }

    try {
      // Build a partial payload including only changed fields
      const patched = {};
      if (hasTimeChange) {
        if (formData.start_time && formData.start_time !== appointment.start_time) patched.start_time = formData.start_time;
        if (formData.end_time && formData.end_time !== appointment.end_time) patched.end_time = formData.end_time;
      }
      if (hasStatusChange) patched.status = formData.status;
      if (hasClientChange) patched.client = formData.client;
      if (hasTeamMemberChange) patched.team_member = formData.team_member;
      if (hasServiceChange) patched.service = formData.service;
      if (hasSalonChange) patched.salon = formData.salon;

      const updateData = {
        appointmentId: appointment.appointment_id,
        patchedAppointmentUpdateRequest: {
          ...patched,
          cancellation_reason: formData.status === 'cancelled' ? formData.cancellation_reason : null
        }
      };

      // Validation simple pour le motif d'annulation
      if (formData.status === 'cancelled' && !formData.cancellation_reason?.trim()) {
        setFieldErrors({ cancellation_reason: t('pro_appointments.validation.cancellation_reason_required') });
        setSnackbarSeverity('error');
        setSnackbarMessage(t('pro_appointments.validation.cancellation_reason_required'));
        setSnackbarOpen(true);
        setValidationMessage(t('pro_appointments.validation.cancellation_reason_required'));
        return;
      }
      setFieldErrors({}); // Reset error if valid

      if (confirmOnSave) {
        await modifyAndConfirm({
          id: appointment.appointment_id,
          start_time: formData.start_time,
          team_member: Number(formData.team_member),
          agreement_confirmed: true,
        }).unwrap();
      } else {
        await updateAppointment(updateData).unwrap();
      }

      // Call success callback and close dialog
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update appointment:', error);
      // Parse backend validation error and show ONLY the primary message
      let message = t('pro_appointments.error.update_failed');
      const data = error?.data || error?.error || {};

      if (data?.start_time) {
        if (Array.isArray(data.start_time) && data.start_time.length > 0) {
          message = typeof data.start_time[0] === 'string' ? data.start_time[0] : String(data.start_time[0]);
        } else if (typeof data.start_time === 'string') {
          message = data.start_time;
        }
      } else if (typeof data?.detail === 'string') {
        message = data.detail;
      } else if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0) {
        message = typeof data.non_field_errors[0] === 'string' ? data.non_field_errors[0] : String(data.non_field_errors[0]);
      } else if (typeof error?.message === 'string') {
        message = error.message;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Content to be displayed in the dialog
  const updateContent = (
    <Box sx={{ padding: '16px 0' }}>
      {validationMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validationMessage}
        </Alert>
      )}

      {/* Salon Selection */}
      <FormControl
        fullWidth
        margin="normal"
        disabled={isUpdating || salonsLoading}
        sx={{ mb: 2 }}
      >
        <InputLabel shrink={Boolean(formData.salon || appointment?.salon_name)}>
          {t('pro_appointments.labels.salon')}
        </InputLabel>
        <Select
          value={formData.salon || ''}
          onChange={handleChange('salon')}
          label={t('pro_appointments.labels.salon')}
          displayEmpty
          notched={Boolean(formData.salon || appointment?.salon_name)}
          renderValue={(selected) => {
            if (!selected && appointment?.salon_name) {
              return appointment.salon_name;
            }
            if (!selected) {
              return t('pro_appointments.placeholders.select_salon');
            }
            // Check if salonsData is an array or if it has a results property
            const salonsArray = Array.isArray(salonsData) ? salonsData : salonsData?.results;
            const selectedSalon = salonsArray?.find(salon => salon.salon_id === selected);
            return selectedSalon ? selectedSalon.name : (appointment?.salon_name || selected);
          }}
        >
          <MenuItem value="">{t('pro_appointments.placeholders.select_salon')}</MenuItem>
          {salonsLoading ? (
            <MenuItem disabled>{t('common.loading')}</MenuItem>
          ) : salonsData ? (
            // Check if salonsData is an array or if it has a results property
            (() => {
              const salonsArray = Array.isArray(salonsData) ? salonsData : salonsData.results || [];

              return salonsArray.length > 0 ? (
                salonsArray.map(salon => (
                  <MenuItem key={salon.salon_id} value={salon.salon_id}>
                    {salon.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>{t('pro_appointments.no_salons')}</MenuItem>
              );
            })()
          ) : (
            <MenuItem disabled>{t('pro_appointments.no_salons')}</MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Client Selection */}
      <FormControl
        fullWidth
        margin="normal"
        disabled={isUpdating || clientsLoading}
        sx={{ mb: 2 }}
      >
        <InputLabel shrink={Boolean(formData.client || appointment?.client_name)}>
          {t('pro_appointments.labels.client')}
        </InputLabel>
        <Select
          value={formData.client && clientsData?.find(client => client.id === formData.client) ? formData.client : ''}
          onChange={handleChange('client')}
          label={t('pro_appointments.labels.client')}
          displayEmpty
          notched={Boolean(formData.client || appointment?.client_name)}
          renderValue={(selected) => {
            if (!selected && appointment?.client_name) {
              return appointment.client_name;
            }
            if (!selected) {
              return t('pro_appointments.placeholders.select_client');
            }
            const selectedClient = clientsData?.find(client => client.id === selected);
            return selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : (appointment?.client_name || selected);
          }}
        >
          <MenuItem value="">{t('pro_appointments.placeholders.select_client')}</MenuItem>
          {clientsLoading ? (
            <MenuItem disabled>{t('common.loading')}</MenuItem>
          ) : clientsData && clientsData.length > 0 ? (
            clientsData.map(client => (
              <MenuItem key={client.id} value={client.id}>
                {client.first_name} {client.last_name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>{t('pro_appointments.no_clients')}</MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Team Member Selection */}
      <FormControl
        fullWidth
        margin="normal"
        disabled={isUpdating || teamMembersLoading}
        sx={{ mb: 2 }}
      >
        <InputLabel shrink={Boolean(formData.team_member || appointment?.team_member_name)}>
          {t('pro_appointments.labels.team_member')}
        </InputLabel>
        <Select
          value={formData.team_member || ''}
          onChange={handleChange('team_member')}
          label={t('pro_appointments.labels.team_member')}
          displayEmpty
          notched={Boolean(formData.team_member || appointment?.team_member_name)}
          renderValue={(selected) => {
            // Si une valeur est sélectionnée, chercher le membre dans les données
            if (selected) {
              const selectedMember = teamMembersData?.find(member => member.team_member_id === selected);
              if (selectedMember) {
                return `${selectedMember.user_details.first_name} ${selectedMember.user_details.last_name}`;
              }
              // Si le membre n'est pas trouvé dans la liste mais qu'on a le nom, l'afficher
              if (appointment?.team_member_name) {
                return appointment.team_member_name;
              }
            }
            // Si pas de sélection mais qu'on a le nom de l'appointment, l'afficher
            if (!selected && appointment?.team_member_name) {
              return appointment.team_member_name;
            }
            // Sinon afficher le placeholder
            return t('pro_appointments.placeholders.select_team_member');
          }}
        >
          <MenuItem value="">{t('pro_appointments.placeholders.select_team_member')}</MenuItem>
          {teamMembersLoading ? (
            <MenuItem disabled>{t('common.loading')}</MenuItem>
          ) : teamMembersData && teamMembersData.length > 0 ? (
            teamMembersData.map(member => (
              <MenuItem key={member.team_member_id} value={member.team_member_id}>
                {member.user_details.first_name} {member.user_details.last_name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>{t('pro_appointments.no_team_members')}</MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Service Selection */}
      <FormControl
        fullWidth
        margin="normal"
        disabled={isUpdating || servicesLoading}
        sx={{ mb: 2 }}
      >
        <InputLabel shrink={Boolean(formData.service || appointment?.service_name)}>
          {t('pro_appointments.labels.service')}
        </InputLabel>
        <Select
          value={formData.service && servicesData?.find(service => service.service_id === formData.service) ? formData.service : ''}
          onChange={handleChange('service')}
          label={t('pro_appointments.labels.service')}
          displayEmpty
          notched={Boolean(formData.service || appointment?.service_name)}
          renderValue={(selected) => {
            if (!selected && appointment?.service_name) {
              return appointment.service_name;
            }
            if (!selected) {
              return t('pro_appointments.placeholders.select_service');
            }
            const selectedService = servicesData?.find(service => service.service_id === selected);
            return selectedService ? `${selectedService.name} - ${selectedService.duration} min` : (appointment?.service_name || selected);
          }}
        >
          <MenuItem value="">{t('pro_appointments.placeholders.select_service')}</MenuItem>
          {servicesLoading ? (
            <MenuItem disabled>{t('common.loading')}</MenuItem>
          ) : servicesData && servicesData.length > 0 ? (
            servicesData.map(service => (
              <MenuItem key={service.service_id} value={service.service_id}>
                {service.name} - {service.duration} min
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>{t('pro_appointments.no_services')}</MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Date Selector */}
      <TextField
        label={t('pro_appointments.labels.select_date')}
        type="date"
        value={moment(selectedDate).format('YYYY-MM-DD')}
        onChange={handleDateChange}
        fullWidth
        sx={{ mb: 2 }}
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          min: moment().format('YYYY-MM-DD')
        }}
      />

      {/* Display selected date and time */}
      {formData.start_time && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t('pro_appointments.labels.selected_appointment')}:</strong> {moment(formData.start_time).format('DD/MM/YYYY à HH:mm')}
          </Typography>
        </Alert>
      )}

      {/* Available time slots */}
      {formData.service && formData.team_member && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('pro_appointments.labels.available_slots')}:
          </Typography>

          {isLoadingSlots ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('pro_appointments.loading_slots')}
              </Typography>
            </Box>
          ) : filteredAvailableSlots.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filteredAvailableSlots.map((slot, index) => {
                const [hours, minutes] = slot.split(':');
                const currentStartTime = formData.start_time ? new Date(formData.start_time) : null;
                const slotDate = new Date(selectedDate);
                slotDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                const isSelected = currentStartTime &&
                  currentStartTime.getTime() === slotDate.getTime();

                return (
                  <Button
                    key={index}
                    variant={isSelected ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleSlotClick(slot)}
                    sx={{
                      minWidth: '80px',
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {slot}
                  </Button>
                );
              })}
            </Box>
          ) : (
            <Alert severity="warning">
              {t('pro_appointments.no_slots_available')}
            </Alert>
          )}
        </Box>
      )}

      {!formData.service || !formData.team_member ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('pro_appointments.select_service_and_team_member')}
        </Alert>
      ) : null}

      <TextField
        select
        label={t('pro_appointments.labels.status')}
        value={formData.status}
        onChange={handleChange('status')}
        fullWidth
        sx={{ mb: 2, mt: 2 }}
      >
        {statusOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {formData.status === 'cancelled' && (
        <TextField
          label={t('pro_appointments.labels.cancellation_reason')}
          value={formData.cancellation_reason}
          onChange={handleChange('cancellation_reason')}
          fullWidth
          multiline
          rows={3}
          required
          placeholder={t('pro_appointments.placeholders.cancellation_reason')}
          sx={{ mb: 2 }}
          error={!!fieldErrors.cancellation_reason}
          helperText={fieldErrors.cancellation_reason}
        />
      )}
    </Box>
  );

  // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
  const triggerButton = <Button style={{ display: 'none' }}>{t('pro_appointments.open')}</Button>;

  // Effect to handle service resolution after salon is selected
  useEffect(() => {
    if (!open || !formData.salon || !appointment?.service_name) return;

    // If we have services data and the service ID hasn't been resolved yet
    if (servicesData && !formData.service) {
      const serviceId = findIdByName(servicesData, 'name', appointment.service_name, 'service_id');
      if (serviceId) {
        setFormData(prev => ({
          ...prev,
          service: serviceId
        }));
      }
    }
  }, [open, formData.salon, servicesData, appointment?.service_name]);


  return (
    <>
      <MuiDialog
        open={open}
        icon={<EditIcon />}
        triggerButton={triggerButton}
        title={t('pro_appointments.update_title')}
        subtitle={t('pro_appointments.update_subtitle', { name: appointment?.client_name || '' })}
        content={updateContent}
        existAction={true}
        actionTitle={t('pro_appointments.update_action')}
        actionFunction={handleUpdate}
        onClose={dialogClosed}
        onExternalClose={onClose}
        isLoading={isUpdating}
        actionType="button"
        maxWidth="md"
        disableAction={(() => {
          if (isUpdating) return true;
          // Allow update if any change is present (especially status change)
          const hasTimeChange = (
            formData.start_time && formData.start_time !== appointment?.start_time
          ) || (
              formData.end_time && formData.end_time !== appointment?.end_time
            );
          const hasStatusChange = formData.status && formData.status !== appointment?.status;
          const hasReasonChange = formData.status === 'cancelled' && formData.cancellation_reason !== appointment?.cancellation_reason;
          const hasOtherChanges = Boolean((formData.client && formData.client !== appointment?.client) ||
            (formData.team_member && formData.team_member !== appointment?.team_member) ||
            (formData.service && formData.service !== appointment?.service) ||
            (formData.salon && formData.salon !== appointment?.salon));
          return !(hasTimeChange || hasStatusChange || hasReasonChange || hasOtherChanges);
        })()}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={undefined}
        sx={{ zIndex: 99999 }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UpdateAppointment;
